import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { CryptoAlertData } from "@crypto-trading/common/browser";
import { useChartCandles } from "./useChartCandles";
import { Interval, INTERVAL_DURATIONS_MS, CHART_INTERVAL_OPTIONS, pickChartInterval } from "./usePositionPriceData";
import { ensureUsdtSymbol } from "@/utils/symbols";
import {
  CANDLE_EXCHANGE_PRIORITY,
  listChartableExchanges,
  mapExchangeToSupportedCode,
} from "@/lib/live-candles/candle-symbols";
import { getClientMarketDataService } from "@/lib/market-data/market-data-service";

const HYPERLIQUID_MAX_CANDLES = 5000;

/**
 * Check if an interval is valid for Hyperliquid's data retention limit
 * Hyperliquid only keeps the most recent 5000 candles
 */
const isIntervalValidForHyperliquid = (interval: Interval, endTime: number): boolean => {
  const now = Date.now();
  const intervalDurationMs = INTERVAL_DURATIONS_MS[interval];
  const maxLookbackMs = HYPERLIQUID_MAX_CANDLES * intervalDurationMs;
  const oldestAvailableTime = now - maxLookbackMs;

  // Check if endTime is within the available data range
  // We check endTime because that's the most recent timestamp in the range
  return endTime >= oldestAvailableTime;
};

interface UseAlertPriceDataParams {
  userId: string;
  alert: CryptoAlertData | null;
  open: boolean;
}

export const useAlertPriceData = ({
  userId,
  alert,
  open,
}: UseAlertPriceDataParams) => {
  const preferredExchange = useMemo(
    () => mapExchangeToSupportedCode(alert?.exchange ?? null),
    [alert?.exchange]
  );

  const symbol = alert
    ? ensureUsdtSymbol(alert.currency.split(",")[0].trim())
    : "";

  const { data: availability, isError: isAvailabilityError } = useQuery({
    queryKey: ["candle-exchange-availability", symbol],
    queryFn: () => getClientMarketDataService().checkTokenAvailableAll(symbol),
    enabled: open && !!symbol,
    staleTime: 5 * 60 * 1000,
  });

  const chartExchanges = useMemo(() => {
    if (availability) {
      return listChartableExchanges(availability, preferredExchange);
    }
    if (!isAvailabilityError) return [];
    return [
      preferredExchange,
      ...CANDLE_EXCHANGE_PRIORITY.filter((code) => code !== preferredExchange),
    ];
  }, [availability, isAvailabilityError, preferredExchange]);

  const exchange = chartExchanges[0] ?? preferredExchange;
  const isResolvingExchange = open && !!symbol && !availability && !isAvailabilityError;

  // Calculate start time for price data (alert creation time)
  const startTime = alert?.createdAt ? new Date(alert.createdAt).getTime() : null;

  // Filter intervals based on Hyperliquid's 5000 candles limit
  // For each interval, calculate the endTime and check if it's within the lookback period
  const enabledIntervals = useMemo(() => {
    if (!startTime) return [];

    const intervals: Interval[] = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];

    return intervals.filter((interval) => {
      if (exchange === "hyperliquid") {
        return isIntervalValidForHyperliquid(interval, startTime);
      }
      return true;
    });
  }, [startTime, exchange]);

  const [selectedInterval, setSelectedInterval] = useState<Interval>("1m");

  const alertId = alert?.id ?? null;

  // Load from alert time through now so interval switches always have full history
  const endTime = useMemo(() => {
    if (!startTime) return null;
    return Date.now();
  }, [startTime]);

  useEffect(() => {
    if (!open || !alertId || enabledIntervals.length === 0) return;
    setSelectedInterval((current) => {
      if (
        CHART_INTERVAL_OPTIONS.includes(current) &&
        enabledIntervals.includes(current)
      ) {
        return current;
      }
      return pickChartInterval(enabledIntervals);
    });
  }, [open, alertId, enabledIntervals]);

  useEffect(() => {
    if (enabledIntervals.length === 0) return;
    setSelectedInterval((current) => {
      if (enabledIntervals.includes(current)) {
        return current;
      }
      return pickChartInterval(enabledIntervals);
    });
  }, [enabledIntervals]);

  const {
    priceChanges,
    isLoading,
    isLoadingMore,
    error,
    historicalUnavailable,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
  } = useChartCandles({
    userId,
    symbol,
    exchange,
    exchanges: chartExchanges,
    interval: selectedInterval,
    startTime,
    endTime,
    enabled:
      open &&
      !!alert &&
      !!userId &&
      !!startTime &&
      !!endTime &&
      !!symbol &&
      chartExchanges.length > 0,
    paddingCandles: 10,
    allowRecentFallback: true,
    focusAnchorTime: startTime,
  });

  return {
    enabledIntervals,
    selectedInterval,
    setSelectedInterval,
    priceChanges,
    isLoading: isLoading || isResolvingExchange,
    isLoadingMore,
    error,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
    endTime,
    startTime,
    symbol,
    exchange,
    isTokenUnavailable: !!availability && chartExchanges.length === 0,
    historicalChartUnavailable:
      (!!availability && chartExchanges.length === 0) || historicalUnavailable,
  };
};

